import { axios, transformAxiosError } from "../utils/axios.create";
import {
  Setting,
  SettingApplication,
  SettingType,
} from "../domain/settings.interface";

export type FindAllSettingsCatalogPayload = {
  search?: string;
  application?: SettingApplication | "";
  module?: string;
  section?: string;
  type?: SettingType | "";
  active?: boolean | "";
  pageNumber: number;
  pageSize: number;
  sortField: string;
  sortOrder: number;
};

export type SettingPayload = {
  product: "pret";
  application: SettingApplication;
  module: string;
  section: string;
  name: string;
  description: string;
  type: SettingType;
  default: unknown;
  active: boolean;
  createdBy?: string;
  updatedBy?: string;
};

export type SettingsCatalogListResponse = {
  list: Setting[];
  totalCount: number;
};

const findAll = async ({
  search,
  application,
  module,
  section,
  type,
  active,
  pageNumber,
  pageSize,
  sortField,
  sortOrder,
}: FindAllSettingsCatalogPayload): Promise<SettingsCatalogListResponse> => {
  const queryParams = new URLSearchParams({
    pageNumber: String(pageNumber + 1),
    pageSize: String(pageSize),
    sortField,
    sortOrder: sortOrder === 1 ? "asc" : "desc",
  });

  if (search) {
    queryParams.append("search", search);
  }

  if (application) {
    queryParams.append("application", application);
  }

  if (module) {
    queryParams.append("module", module);
  }

  if (section) {
    queryParams.append("section", section);
  }

  if (type) {
    queryParams.append("type", type);
  }

  if (active !== "" && active !== undefined) {
    queryParams.append("active", String(active));
  }

  return axios
    .get(`/settings-catalog?${queryParams.toString()}`)
    .then((res) => {
      const body = res.data.body;
      return {
        list: body?.list || [],
        totalCount: body?.totalCount || 0,
      };
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const findOne = async (id: string): Promise<Setting> => {
  return axios
    .get(`/settings-catalog/find/${id}`)
    .then((res) => {
      return res.data.body as Setting;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const create = async (data: SettingPayload): Promise<Setting> => {
  return axios
    .post("/settings-catalog", data)
    .then((res) => {
      return res.data.body as Setting;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const update = async ({
  id,
  data,
}: {
  id: string;
  data: SettingPayload;
}): Promise<Setting> => {
  return axios
    .put(`/settings-catalog/${id}`, data)
    .then((res) => {
      return res.data.body as Setting;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const archive = async (id: string): Promise<void> => {
  return axios
    .patch(`/settings-catalog/archive/${id}`)
    .then(() => {
      return;
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

export const SettingsCatalogService = {
  findAll,
  findOne,
  create,
  update,
  archive,
};
